/**
 * Piezas de etiquetas que usa la Biblioteca (Lo mío + la ficha): el chip de una
 * etiqueta, el selector para asignarlas, el gestor (crear / renombrar /
 * ordenar / borrar) y el diálogo de confirmación. Vienen del módulo de
 * Vocabulario, cuya pantalla se absorbió en Lo mío y se borró (card #348).
 */
import { useEffect, useRef, useState } from 'react';
import { TAG_COLORS, TAG_EMOJIS, type VocabEntry, type VocabTag } from './vocabData.js';
import { AIconCheck, AIconClose, AIconGrip, AIconPencil, AIconPlus, AIconSearch, AIconTrash } from './icons.js';
import { useSheetDrag } from '../../hooks/useSheetDrag.js';

export function TagBadge({
  tag,
  mini = false,
  onRemove,
}: {
  tag: VocabTag | undefined;
  mini?: boolean;
  onRemove?: (id: string) => void;
}) {
  if (!tag) return null;
  return (
    <span className={`vt-tag ${mini ? 'mini' : ''}`} style={{ borderColor: `${tag.color}55`, background: `${tag.color}14`, color: tag.color }}>
      <span className="vt-tag-emoji">{tag.emoji}</span>
      <span>{tag.name}</span>
      {onRemove && (
        <button className="vt-tag-x" onClick={(e) => { e.stopPropagation(); onRemove(tag.id); }} aria-label={`Quitar ${tag.name}`}>
          <AIconClose size={9} />
        </button>
      )}
    </span>
  );
}

// ─── Tag picker (assign tags) — multi-select + create ──────────────────
export function TagPicker({
  tags,
  value,
  onToggle,
  onCreate,
}: {
  tags: readonly VocabTag[];
  value: string[];
  onToggle: (id: string) => void;
  onCreate: (name: string) => void;
}) {
  const [q, setQ] = useState('');
  const term = q.trim().toLowerCase();
  const matches = tags.filter((t) => t.name.toLowerCase().includes(term));
  const exact = tags.some((t) => t.name.toLowerCase() === term);
  return (
    <div className="vtagpick">
      <div className="vocab-search sm">
        <AIconSearch size={13} color="var(--text-faint)" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar o crear etiqueta…" />
      </div>
      <div className="vtagpick-list">
        {matches.map((t) => {
          const on = value.includes(t.id);
          return (
            <button key={t.id} className={`vtagpick-item ${on ? 'on' : ''}`} onClick={() => onToggle(t.id)}>
              <span className="vt-tag-emoji">{t.emoji}</span>
              <span className="vtagpick-name">{t.name}</span>
              <span className="vocab-cat-dot" style={{ background: t.color }} />
              <span className={`vtagpick-check ${on ? 'on' : ''}`}>{on && <AIconCheck size={11} color="#0c0d15" />}</span>
            </button>
          );
        })}
        {term && !exact && (
          <button className="vtagpick-create" onClick={() => { onCreate(q.trim()); setQ(''); }}>
            <AIconPlus size={13} color="var(--lime)" /> Crear “{q.trim()}”
          </button>
        )}
        {!matches.length && !term && <div className="vtagpick-empty">Sin etiquetas todavía.</div>}
      </div>
    </div>
  );
}

// ─── Tag manager — create / rename / reorder / delete ──────────────────
// Sólo administra: filtrar por etiqueta es el botón de filtros de Lo mío.
export function TagManager({
  tags,
  vocab,
  onClose,
  onCreate,
  onRename,
  onDelete,
  onReorder,
}: {
  tags: readonly VocabTag[];
  vocab: readonly VocabEntry[];
  onClose: () => void;
  onCreate: (name: string, emoji: string, color: string) => void;
  onRename: (id: string, name: string) => void;
  onDelete: (id: string, deleteEntries: boolean) => void;
  onReorder: (dragId: string, targetId: string) => void;
}) {
  const { grabProps, sheetStyle } = useSheetDrag(onClose);
  const [draft, setDraft] = useState('');
  const [emoji, setEmoji] = useState(TAG_EMOJIS[4]!);
  const [color, setColor] = useState(TAG_COLORS[0]!);
  const [editing, setEditing] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState<VocabTag | null>(null);
  const dragId = useRef<string | null>(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const countFor = (id: string) => vocab.filter((w) => w.tags.includes(id)).length;
  const create = () => { if (draft.trim()) { onCreate(draft.trim(), emoji, color); setDraft(''); } };

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div
        className="settings-sheet"
        onClick={(e) => e.stopPropagation()}
        style={{ ...sheetStyle, maxHeight: '88%' }}
      >
        <div className="settings-handle sheet-grab" {...grabProps} />
        <div className="settings-scroll" style={{ paddingBottom: 24 }}>
          <div className="ws-head" style={{ paddingBottom: 10 }}>
            <span className="script" style={{ fontSize: 30, color: 'var(--text)' }}>Tus etiquetas</span>
            <button className="settings-x" onClick={onClose} aria-label="Cerrar"><AIconClose size={14} /></button>
          </div>

          <div className="vtagmgr-create">
            <div className="vtagmgr-row">
              <span className="vtagmgr-emoji-preview" aria-hidden="true">{emoji}</span>
              <input className="vtagmgr-input" value={draft} placeholder="Nombre de la etiqueta…" onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && create()} />
              <button className="vf-primary sm" onClick={create} disabled={!draft.trim()}>Crear</button>
            </div>
            {/* Grilla de emojis propia (NO <select> nativo: su dropdown del SO
                se renderizaba fuera del sheet en mobile). */}
            <div className="vtagmgr-emojis hide-scroll" role="radiogroup" aria-label="Ícono de la etiqueta">
              {TAG_EMOJIS.map((e) => (
                <button
                  key={e}
                  type="button"
                  className={`vtagmgr-emoji-opt ${emoji === e ? 'on' : ''}`}
                  onClick={() => setEmoji(e)}
                  role="radio"
                  aria-checked={emoji === e}
                  aria-label={`Ícono ${e}`}
                >
                  {e}
                </button>
              ))}
            </div>
            <div className="vtagmgr-colors">
              {TAG_COLORS.map((c) => (
                <button key={c} className={`vtagmgr-color ${color === c ? 'on' : ''}`} style={{ background: c }} onClick={() => setColor(c)} aria-label={c} />
              ))}
            </div>
          </div>

          <div className="vtagmgr-list">
            {tags.map((t) => (
              <div
                key={t.id}
                className="vtagmgr-item"
                draggable
                onDragStart={() => { dragId.current = t.id; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (dragId.current && dragId.current !== t.id) onReorder(dragId.current, t.id); dragId.current = null; }}
              >
                <span className="vtagmgr-grip"><AIconGrip size={14} color="var(--text-faint)" /></span>
                <span className="vt-tag-emoji" style={{ fontSize: 16 }}>{t.emoji}</span>
                {editing === t.id ? (
                  <input
                    className="vtagmgr-input flex"
                    autoFocus
                    defaultValue={t.name}
                    onBlur={(e) => { onRename(t.id, e.target.value.trim() || t.name); setEditing(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { onRename(t.id, (e.target as HTMLInputElement).value.trim() || t.name); setEditing(null); } }}
                  />
                ) : (
                  <span className="vtagmgr-name" onClick={() => setEditing(t.id)}>{t.name}</span>
                )}
                <span className="vocab-cat-dot" style={{ background: t.color }} />
                <span className="vtagmgr-count">{countFor(t.id)}</span>
                <button className="vtagmgr-edit" onClick={() => setEditing(t.id)} aria-label="Renombrar"><AIconPencil size={13} /></button>
                <button className="vtagmgr-del" onClick={() => setConfirmDel(t)} aria-label="Borrar"><AIconTrash size={14} /></button>
              </div>
            ))}
            {!tags.length && <div className="vtagpick-empty">No tenés etiquetas. Creá una arriba.</div>}
          </div>
        </div>

        {confirmDel && (
          <ConfirmDialog
            title={`Borrar “${confirmDel.name}”`}
            body={`¿Borrar solo la etiqueta o también las ${countFor(confirmDel.id)} entradas que tiene? Por defecto solo se borra la etiqueta; las entradas quedan.`}
            actions={[
              { label: 'Cancelar', kind: 'ghost', on: () => setConfirmDel(null) },
              { label: 'Solo la etiqueta', kind: 'primary', on: () => { onDelete(confirmDel.id, false); setConfirmDel(null); } },
              { label: 'Etiqueta + entradas', kind: 'danger', on: () => { onDelete(confirmDel.id, true); setConfirmDel(null); } },
            ]}
          />
        )}
      </div>
    </div>
  );
}

// ─── Confirm dialog ────────────────────────────────────────────────────
export function ConfirmDialog({
  title,
  body,
  actions,
}: {
  title: string;
  body: string;
  actions: Array<{ label: string; kind: 'ghost' | 'primary' | 'danger'; on: () => void }>;
}) {
  return (
    <div className="vconfirm-overlay" onClick={(e) => e.stopPropagation()}>
      <div className="vconfirm">
        <div className="vconfirm-title">{title}</div>
        <div className="vconfirm-body">{body}</div>
        <div className="vconfirm-actions">
          {actions.map((a, i) => (
            <button key={i} className={`vconfirm-btn ${a.kind}`} onClick={a.on}>{a.label}</button>
          ))}
        </div>
      </div>
    </div>
  );
}
